import { Button, MenuItem, Stack, SxProps, Theme, Typography } from "@mui/material";
import { omit } from "lodash-es";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import ListViewLayout from "@/App/Layouts/ListViewLayout";
import SimpleViewPageHeader from "@/App/Layouts/PageHeader/SimpleViewPageHeader";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataExportModal from "@/common/components/DataExport/DataExportModal";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import Datetime from "@/common/components/Datetime/Datetime";
import InlineUser from "@/common/components/Entity/User/InlineUser";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import BooleanValue from "@/common/components/Form/Display/BooleanValue";
import AppIcon from "@/common/components/Icons/AppIcon";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import TableCellContentOfAffiliation from "@/common/components/Table/TableCell/TableCellContentOfAffiliation";
import TableCellContentOfAffiliationInDepartments from "@/common/components/Table/TableCell/TableCellContentOfAffiliationInDepartments";
import TableCellContentOfAffiliationInLocations from "@/common/components/Table/TableCell/TableCellContentOfAffiliationInLocations";
import TableCellContentOfAttachments from "@/common/components/Table/TableCell/TableCellContentOfAttachments";
import TableCellContentOfCount from "@/common/components/Table/TableCell/TableCellContentOfCount";
import TableCellContentOfTags from "@/common/components/Table/TableCell/TableCellContentOfTags";
import { ROUTE_PATH } from "@/common/constants/routing";
import { FilterCatalog } from "@/common/filters/filterCatalog";
import { PermissionHelper } from "@/common/helpers/authorization/permission";
import { EntityHelper } from "@/common/helpers/entity";
import { TagStaticFilterInputDto, TagsHelper } from "@/common/helpers/entity/tags";
import { TabularHelper } from "@/common/helpers/tabular";
import { getTypedPath } from "@/common/helpers/typedPath";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCommonRequestParams } from "@/common/hooks/api/useCommonRequestParams";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { PropagatedDeps } from "@/common/hooks/render/usePropagatedDeps";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ApiEnumName } from "@/common/services/enum";
import { FilterFieldType } from "@/common/ts/filters";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DamageDetectionDto,
  DamageDetectionGetPaginatedDto,
  DataUpdatesHubClientMethodName,
  EntityOriginType,
  EntityType,
  ExportType,
  GeneralAttachedTagsInputDto,
  GeneralAttachmentDto,
  TagEntityType,
} from "@/core/api/generated/v1";

import AssignmentInline from "../../Assignment/AssignmentInline";
import ContractInline from "../../Contract/ContractInline";
import TenantStructureMembershipOfEntityEditModal from "../../EntityAffiliation/BulkUpdate/TenantStructureMembershipOfEntityEditModal";
import GeneralInspectorDisplay from "../../General/Display/GeneralInspectorDisplay";
import GeneralAttachedTagsOfEntitiesEditModal from "../../General/GeneralTag/GeneralAttachedTagsOfEntitiesEditModal";
import VehicleInline from "../../Vehicle/VehicleInline";
import VehicleMileageInline from "../../Vehicle/VehicleMileageInline";
import VehicleVisualModelInline from "../../VehicleVisualModel/VehicleVisualModelInline";
import { DamageDetectionCostEvaluationOverviewDisplay } from "../DamageDetectionCostEvaluationOverviewDisplay";
import DamageDetectionMenu from "../DamageDetectionMenu";
import DamageDetectionsDeleteModal from "../DamageDetectionsDeleteModal";

enum BulkActionFlags {
  UpdateAffiliation = "UpdateAffiliation",
  UpdateTags = "UpdateTags",
  Export = "Export",
  Delete = "Delete",
}
const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  filters: true,
  create: true,
  edit: true,
  delete: true,
  viewVariant: ViewVariant.Page,
};

const defaultPropagatedDeps = {
  refetch: {},
};
export type DamageDetectionPaginatedListPropagatedDepsMap = typeof defaultPropagatedDeps;

type DamageDetectionGetPaginatedInputDto = Omit<DamageDetectionGetPaginatedDto, "tags"> & {
  tags?: TagStaticFilterInputDto;
};

export interface DamageDetectionPaginatedListOwnProps {
  displayProps?: Partial<typeof defaultDisplayProps>;
  defaultValues?: {
    limit?: number;
    vehicleId?: string | null;
    contractId?: string | null;
    customerId?: string | null;
    anyVehicleDamageIds?: string[] | null;
  };
  propagatedDeps?: PropagatedDeps<DamageDetectionPaginatedListPropagatedDepsMap>;
  sx?: SxProps<Theme>;
}

export type DamageDetectionPaginatedListProps = DamageDetectionPaginatedListOwnProps;

export default function DamageDetectionPaginatedList({
  displayProps,
  defaultValues,
  propagatedDeps,
  sx,
}: DamageDetectionPaginatedListProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();
  const commonRequestParams = useCommonRequestParams<DamageDetectionGetPaginatedInputDto>({
    statePersistence: {
      persistenceKey: EntityType.DamageDetection,
    },
    defaultValues: {
      limit: defaultValues?.limit,
      params: {
        ...defaultValues,
      },
    },
    initParams: (filterDefinition) => {
      const tagFilter = filterDefinition.filterDefinition?.items.find(
        (x) => x.field === getTypedPath<DamageDetectionDto>().tags.$path,
      );

      return {
        tags: {
          operator: tagFilter?.operator,
          query: tagFilter?.value as GeneralAttachedTagsInputDto,
        } as TagStaticFilterInputDto,
      };
    },
    options: { useUrlSearchParamsToStoreSelectedPage: true },
  });

  const [isDataExportModalOpened, setIsDataExportModalOpened] = useState(false);

  const paginatedDamageDetectionsRequest = useApiRequest(
    apiClient.damageDetectionsApi.apiV1DamageDetectionsGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      damageDetectionGetPaginatedDto: {
        ...omit(commonRequestParams.params, "tags"),
        offset: commonRequestParams.offset,
        limit: commonRequestParams.limit,
        search: commonRequestParams.search,
        tags:
          commonRequestParams.filterDefinition?.items
            .filter((x) => x.field === getTypedPath<DamageDetectionDto>().tags.$path)
            .map((x) => TagsHelper.mapFromFilter(x))[0] || undefined,
        sortDefinition: commonRequestParams.sortDefinitionDto,
        filterDefinition: commonRequestParams.filterDefinitionDto,
      },
    },
    {
      deps: [
        ...commonRequestParams.deps,
        commonRequestParams.filterDefinition,
        propagatedDeps?.depsMap["refetch"],
      ],
      debouncedDeps: {
        deps: [...commonRequestParams.debouncedDeps],
        wait: 500,
        options: { leading: false, trailing: true },
      },
      commonRequestParams: commonRequestParams,
    },
  );
  const paginatedDamageDetections = useMemo(
    () => paginatedDamageDetectionsRequest?.data,
    [paginatedDamageDetectionsRequest.data],
  );

  const damageDetectionIds = useMemo(
    () => paginatedDamageDetections?.items?.map((item) => item.id!),
    [paginatedDamageDetections?.items],
  );

  useRealtimeDataUpdates({
    channelNames: [DataUpdatesChannelName.Entities(currentTenant?.id, EntityType.DamageDetection)],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      paginatedDamageDetectionsRequest.handleEntityChanged(data);
    },
  });

  return (
    <ListViewLayout
      displayProps={displayProps}
      header={
        displayProps?.header && (
          <SimpleViewPageHeader
            title='Damage detections'
            primaryActions={
              displayProps?.create && (
                <AuthorizedElement permissions={[AppPermission.DamageDetectionManage]}>
                  <Button
                    variant='contained'
                    color='primary'
                    startIcon={<AppIcon of='add' />}
                    component={RouterLink}
                    to={ROUTE_PATH.DAMAGE_DETECTION_CREATE({
                      vehicleId: commonRequestParams.params?.vehicleId,
                      contractId: commonRequestParams.params?.contractId,
                    })}
                  >
                    Create new damage detection
                  </Button>
                </AuthorizedElement>
              )
            }
            secondaryActions={
              <>
                <MenuWithTrigger
                  withAuthCloseOnClick
                  trigger={
                    <AppIconButton sx={{ ml: "auto" }}>
                      <AppIcon of='moreVert' />
                    </AppIconButton>
                  }
                >
                  {/* <MenuItem component={RouterLink} to={ROUTE_PATH.DOCUMENTS_DAMAGE_DETECTIONS()}>
                      <AppIcon of='documents' fontSize='small' sx={{ mr: 1 }} />
                      <Typography component='span'>View documents</Typography>
                    </MenuItem> */}
                  <MenuItem component={RouterLink} to={ROUTE_PATH.DAMAGE_DETECTION_AGGREGATES}>
                    <AppIcon of='damageDetections' fontSize='small' sx={{ mr: 1 }} />
                    <Typography component='span'>View aggregated</Typography>
                  </MenuItem>
                  <AuthorizedElement permissions={[AppPermission.ExportManage]}>
                    <MenuItem onClick={() => setIsDataExportModalOpened(true)}>
                      <AppIcon of='export' fontSize='small' sx={{ mr: 1 }} />
                      <Typography component='span'>Export damage detections</Typography>
                    </MenuItem>
                  </AuthorizedElement>
                </MenuWithTrigger>
              </>
            }
          />
        )
      }
    >
      <DataTabular
        columns={[
          TabularHelper.getColumnForEntityChips<DamageDetectionDto>(),
          {
            field: getTypedPath<DamageDetectionDto>().id.$path,
            title: "ID",
            width: 250,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.id,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().localNumber.$path,
            title: "Number",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <Stack direction='row' spacing={0.5}>
                <span>{item.localNumber}</span>
              </Stack>
            ),
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().vehicle.id.$path,
            title: "Vehicle",
            width: 300,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => <VehicleInline entity={item.vehicle} />,
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfVehicle(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().contract.id.$path,
            title: "Contract",
            width: 300,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => (item.contract ? <ContractInline entity={item.contract} /> : "-"),
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfContract(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().externalNumber.$path,
            title: "Ext. number",
            description: "External number",
            width: 100,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.externalNumber || "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().assignment.id.$path,
            title: "Assignment",
            width: 300,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.assignment ? <AssignmentInline entity={item.assignment} /> : "-",
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfAssignment(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().vehicleVisualModel.id.$path,
            title: "Visual model",
            width: 300,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.vehicleVisualModel ? (
                <VehicleVisualModelInline entity={item.vehicleVisualModel} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfVehicleVisualModel(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().inspectedAt.$path,
            title: "Inspected at",
            width: 200,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.inspectedAt ? <Datetime datetime={item.inspectedAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().inspector.$path,
            title: "Inspector",
            width: 200,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            renderCell: (item) =>
              item.inspector && (
                <GeneralInspectorDisplay
                  inspector={item.inspector}
                  inlineUserProps={{
                    withLink: false,
                    size: "full",
                  }}
                />
              ),
          },
          {
            field: getTypedPath<DamageDetectionDto>().mileage.$path,
            title: "Mileage",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => <VehicleMileageInline value={item.mileage} />,
            filters: {
              fieldType: FilterFieldType.Number,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().fuelLevel.$path,
            title: "Fuel level",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.fuelLevel ?? "-",
            filters: {
              fieldType: FilterFieldType.Number,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().appType.$path,
            title: "App type",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            isToDisabled: false,
            renderCell: (item) =>
              item.appType ? (
                <InlineApiEnumValue
                  type={ApiEnumName.FrontEndAppType}
                  value={item.appType}
                  withHelperTooltip
                />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.FrontEndAppType,
                },
              },
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().notes.$path,
            title: "Notes",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => item.notes || "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().costEvaluationOverview.$path,
            title: "Cost evaluations",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            renderCell: (item) => (
              <DamageDetectionCostEvaluationOverviewDisplay
                variant='tableCell'
                damageDetection={item}
                overview={item.costEvaluationOverview}
              />
            ),
            filters: {
              fieldType: FilterFieldType.Boolean,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().costEvaluationOverview.localCount.$path,
            title: "Local cost evaluations",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <DamageDetectionCostEvaluationOverviewDisplay
                variant='tableCell'
                damageDetection={item}
                overview={item.costEvaluationOverview}
                originType={EntityOriginType.Local}
              />
            ),
            filters: {
              fieldType: FilterFieldType.Number,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().costEvaluationOverview.externalCount.$path,
            title: "External cost evaluations",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <DamageDetectionCostEvaluationOverviewDisplay
                variant='tableCell'
                damageDetection={item}
                overview={item.costEvaluationOverview}
                originType={EntityOriginType.External}
              />
            ),
            filters: {
              fieldType: FilterFieldType.Number,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().costEvaluationOverview.isAnyLocal.$path,
            title: "Sent to local cost evaluation",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => <BooleanValue value={item.costEvaluationOverview?.isAnyLocal} />,
            filters: {
              fieldType: FilterFieldType.Boolean,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().costEvaluationOverview.isAnyExternal.$path,
            title: "Sent to external cost evaluation",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <BooleanValue value={item.costEvaluationOverview?.isAnyExternal} />
            ),
            filters: {
              fieldType: FilterFieldType.Boolean,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().createdAt.$path,
            title: "Created at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.createdAt ? <Datetime datetime={item.createdAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().updatedAt.$path,
            title: "Updated at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.updatedAt ? <Datetime datetime={item.updatedAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().createdBy.$path,
            title: "Created by",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.createdBy ? <InlineUser userId={item.createdBy} withAvatar /> : "-",
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().updatedBy.$path,
            title: "Updated by",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.updatedBy ? <InlineUser userId={item.updatedBy} withAvatar /> : "-",
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().items.$path,
            title: "Items",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            isToDisabled: true,
            renderCell: (item) => <TableCellContentOfCount count={item.items?.length} />,
          },
          {
            field: getTypedPath<DamageDetectionDto>().attachments.$path,
            title: "Attachments",
            width: 120,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            isToDisabled: true,
            // merge into one array: item.attachments and item.items[N].attachments
            renderCell: (item) => (
              <TableCellContentOfAttachments
                attachments={[
                  ...(item.attachments || []),
                  ...(item.items?.reduce(
                    (acc, itm) => acc.concat(itm.attachments || []),
                    [] as GeneralAttachmentDto[],
                  ) || []),
                ]}
              />
            ),
          },
          {
            field: "affiliation",
            title: "Affiliation",
            description: "Affiliation to departments & locations",
            width: 140,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            isToDisabled: true,
            renderCell: (item) => <TableCellContentOfAffiliation entity={item} />,
          },
          {
            field: getTypedPath<DamageDetectionDto>().departmentIds.$path,
            title: "Departments",
            width: 120,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isToDisabled: false,
            renderCell: (item) => <TableCellContentOfAffiliationInDepartments entity={item} />,
            filters: {
              fieldType: FilterFieldType.ArrayOfId,
              operators: FilterCatalog.getOperatorsForIdFieldOfDepartment(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().locationIds.$path,
            title: "Locations",
            width: 120,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isToDisabled: false,
            renderCell: (item) => <TableCellContentOfAffiliationInLocations entity={item} />,
            filters: {
              fieldType: FilterFieldType.ArrayOfId,
              operators: FilterCatalog.getOperatorsForIdFieldOfLocation(),
            },
          },
          {
            field: getTypedPath<DamageDetectionDto>().tags.$path,
            title: "Tags",
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isColumnMenuDisabled: false,
            isToDisabled: true,
            renderCell: (item) => (
              <TableCellContentOfTags
                entityType={TagEntityType.DamageDetection}
                entity={item}
                onUpdate={(newValue) =>
                  paginatedDamageDetectionsRequest.updateData((data) => {
                    data.items?.forEach((item2) => {
                      if (item2.id === item.id) {
                        item2.tags = newValue || undefined;
                      }
                    });
                  })
                }
              />
            ),
            filters: {
              fieldType: FilterFieldType.Arbitrary,
              isUseSingleFilter: true,
              operators: FilterCatalog.getOperatorsForTagsField(),
            },
          },
        ]}
        rows={paginatedDamageDetections?.items}
        isLoading={paginatedDamageDetectionsRequest.isLoading}
        getRowId={(item) => item.id!}
        rowTo={(item) => ROUTE_PATH.DAMAGE_DETECTION_VIEW(item.id)}
        renderRowAction={({ item }) => (
          <DamageDetectionMenu
            entity={item}
            onDelete={() => paginatedDamageDetectionsRequest.refetch()}
            onUpdate={() => paginatedDamageDetectionsRequest.refetch()}
            displayProps={{
              actions: {
                edit: displayProps?.edit || true,
                delete: displayProps?.delete || false,
                addToContract: true,
                addToAssignment: true,
                downloadPdf: false,
                migrateToNewVisualModel: false,
                viewAllDamages: false,
                createDamageCostEvaluation: false,
                createRepairOperation: false,
                sendToExternalDamageCostEvaluation: false,
              },
            }}
          />
        )}
        statePersistence={commonRequestParams.dataTabularProps.statePersistence}
        pagination={commonRequestParams.dataTabularProps.pagination}
        sort={commonRequestParams.dataTabularProps.sort}
        quickFilter={commonRequestParams.dataTabularProps.quickFilter}
        filters={commonRequestParams.dataTabularProps.filters}
        refetch={commonRequestParams.dataTabularProps.refetch}
        bulkActions={{
          enabled: true,
          definition: BulkActionFlags,
          actionTriggers: ({ startAction }) => (
            <>
              <AuthorizedElement
                permissions={PermissionHelper.presets.entityAffiliationManage(
                  AppPermission.DamageDetectionManage,
                )}
              >
                <AppIconButton
                  tooltipProps={{ title: "Update affiliation" }}
                  onClick={() => startAction(BulkActionFlags.UpdateAffiliation)}
                >
                  <AppIcon of='department' />
                </AppIconButton>
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.DamageDetectionManage]}>
                <AppIconButton
                  tooltipProps={{ title: "Update tags" }}
                  onClick={() => startAction(BulkActionFlags.UpdateTags)}
                >
                  <AppIcon of='tag' />
                </AppIconButton>
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.DamageDetectionManage]}>
                <AppIconButton
                  tooltipProps={{ title: "Delete" }}
                  onClick={() => startAction(BulkActionFlags.Delete)}
                >
                  <AppIcon of='delete' />
                </AppIconButton>
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.ExportManage]}>
                <AppIconButton
                  tooltipProps={{ title: "Export" }}
                  onClick={() => startAction(BulkActionFlags.Export)}
                >
                  <AppIcon of='export' />
                </AppIconButton>
              </AuthorizedElement>
            </>
          ),
          actionHandlers: ({ selectedIds, currentAction, cancelAction, completeAction }) => {
            const selectedEntities = EntityHelper.filterEntitiesByIds(
              paginatedDamageDetections?.items || [],
              selectedIds as string[],
            );
            return (
              <>
                <DamageDetectionsDeleteModal
                  entities={selectedEntities}
                  open={currentAction === BulkActionFlags.Delete}
                  onClose={() => cancelAction()}
                  onDelete={() => {
                    completeAction();
                    paginatedDamageDetectionsRequest.refetch();
                  }}
                />
                <TenantStructureMembershipOfEntityEditModal
                  open={currentAction === BulkActionFlags.UpdateAffiliation}
                  onClose={() => cancelAction()}
                  tenantStructureMembershipOfEntityEditProps={{
                    entityType: EntityType.DamageDetection,
                    entities: selectedEntities,
                    onSave: () => {
                      completeAction();
                      paginatedDamageDetectionsRequest.refetch();
                    },
                  }}
                />
                <GeneralAttachedTagsOfEntitiesEditModal
                  tagEntityType={TagEntityType.DamageDetection}
                  entities={selectedEntities}
                  open={currentAction === BulkActionFlags.UpdateTags}
                  onClose={() => cancelAction()}
                  onSaved={() => {
                    completeAction();
                    paginatedDamageDetectionsRequest.refetch();
                  }}
                />
                <DataExportModal
                  open={currentAction === BulkActionFlags.Export}
                  onClose={() => cancelAction()}
                  dataExportProps={{
                    type: ExportType.DamageDetection,
                    currentPageScope: {
                      ids: selectedIds as string[],
                    },
                    onExportDone: completeAction,
                    displayProps: {
                      scope: false,
                    },
                  }}
                />
              </>
            );
          },
        }}
      />

      {/* Export data modal */}
      <DataExportModal
        open={isDataExportModalOpened}
        onClose={() => setIsDataExportModalOpened(false)}
        dataExportProps={{
          type: ExportType.DamageDetection,
          currentPageScope: {
            ids: damageDetectionIds,
          },
          allFilteredScope: {
            damageDetection:
              paginatedDamageDetectionsRequest.requestParams?.damageDetectionGetPaginatedDto,
          },
          onExportDone: () => {
            setIsDataExportModalOpened(false);
          },
        }}
      />
    </ListViewLayout>
  );
}
